import { parseISO, startOfDay, subDays, subMonths } from 'date-fns';
import { StandardPeriodType } from '../../types/filters/StandardPeriodType';

/** Wire/JSON dates arrive as strings; Calendar/filters need local midnight Date. */
export const toLocalDay = (value: Date | string): Date | undefined => {
  const parsed = typeof value === 'string' ? parseISO(value) : value;
  if (!(parsed instanceof Date) || isNaN(parsed.getTime())) {
    return undefined;
  }
  return startOfDay(parsed);
};

// The single formula for the default window (LAST_30_DAYS). Call it WITHOUT oldestDate when the
// project's data bounds are unknown (e.g. /date-range failed) — same window, just unclamped.
// Do not substitute getDefaultRange() (subMonths(1)): its window is ~2 days wider, and mixing the
// two produced requests with mismatched dates.
export const calculateDefaultDateRange = (
  oldestDate?: Date
): { period: StandardPeriodType; range: Date[] } => {
  const today = startOfDay(new Date());
  const startDate = subDays(today, 29);
  const selectedPeriodValue: StandardPeriodType = 'LAST_30_DAYS';

  // Clamp to the project's first day with data when it is newer than the computed start.
  const defaultOldestDate = oldestDate && oldestDate > startDate ? oldestDate : startDate;

  return {
    period: selectedPeriodValue,
    range: [defaultOldestDate, today],
  };
};

/** Range shortcuts in the panel filters; `CUSTOM` computes no range — it opens the calendar. */
export const standardPeriodOptions = (
  translate: (key: string) => string
): Array<{ label: string; value: StandardPeriodType }> => [
  { label: translate('last7Days'), value: 'LAST_7_DAYS' },
  { label: translate('last14Days'), value: 'LAST_14_DAYS' },
  { label: translate('last30Days'), value: 'LAST_30_DAYS' },
  { label: translate('last3Months'), value: 'LAST_3_MONTHS' },
  { label: translate('customPeriod'), value: 'CUSTOM' },
];

export const calculateStandardRange = (period: string): Date[] | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (period as StandardPeriodType) {
    case 'LAST_7_DAYS':
      return [subDays(today, 6), today];
    case 'LAST_14_DAYS':
      return [subDays(today, 13), today];
    case 'LAST_30_DAYS':
      return [subDays(today, 29), today];
    case 'LAST_3_MONTHS':
      return [subMonths(today, 3), today];
    default:
      return null;
  }
};
